import type { CrossSectionDataSet, SensorDataContext } from "./d";
import {
  generateEquipmentDataPoints,
  generateCrossSectionDataSetTitle
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import { getYToolBypassPositioning } from "./getYToolChartDataSet";

/**
 * Generates datasets representing the sensor cross-sections
 *
 * @param context The cross-section chart context
 * @returns An array of datasets representing the sensors
 */
export const getSensorChartDataSet = (
  sensorDataContext: SensorDataContext
): CrossSectionDataSet[] => {
  const sensorChartDataSet: CrossSectionDataSet[] = [];
  // Radius =$Inputs.F4/2, Center X, Center Y
  const casingInnerRadius = sensorDataContext.casing.innerDiameterIn / 2;
  const casingInnerCenterX = 0;
  const casingInnerCenterY = 0;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    casingInnerCenterX,
    casingInnerCenterY
  );
  const casingDriftRadius = sensorDataContext.casing.driftDiameterIn / 2;
  const casingDriftCenterX = 0;
  const casingDriftCenterY = 0;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    casingDriftCenterX,
    casingDriftCenterY
  );
  sensorChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing ID",
      sensorDataContext.casing.innerDiameterIn,
      sensorDataContext.lengthUnit
    ),
    xAxisLabel: `${sensorDataContext.lengthUnit.label}`,
    yAxisLabel: `${sensorDataContext.lengthUnit.label}`,
    points: casingInnerPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, sensorDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, sensorDataContext.lengthUnit)
      };
    })
  });
  sensorChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing Drift ID",
      sensorDataContext.casing.driftDiameterIn,
      sensorDataContext.lengthUnit
    ),
    xAxisLabel: `${sensorDataContext.lengthUnit.label}`,
    yAxisLabel: `${sensorDataContext.lengthUnit.label}`,
    points: casingDriftPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, sensorDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, sensorDataContext.lengthUnit)
      };
    })
  });
  // Sensor Dim circle
  // Sensor hangs off of motor so we use motor dims here
  const sensorDimRadius = sensorDataContext.outerDiameterIn / 2; // =$Inputs.D27/2 == sensor?.ODinches
  const maxEquipmentOuterDiameterIn =
    sensorDataContext.maxEquipmentOuterDiameterIn || 0;
  let sensorDimCenterX: number;
  const sensorDimCenterY = 0;

  if (
    sensorDataContext.hasYTool &&
    sensorDataContext.bypassTubingOuterDiameterIn &&
    sensorDataContext.yToolWidthIn
  ) {
    // Y-Tool Bypass Tubing Dim circle - use shared positioning logic
    const bypassPositioning = getYToolBypassPositioning({
      casingDriftRadius,
      yToolWidthIn: sensorDataContext.yToolWidthIn,
      bypassTubingOuterDiameterIn:
        sensorDataContext.bypassTubingOuterDiameterIn,
      lengthUnit: sensorDataContext.lengthUnit
    });
    sensorChartDataSet.push(bypassPositioning.bypassTubingDataSet);

    // Position sensor using Excel formula: ='Y-Tool Cross'!K4
    // Max Equipment Center X = bypassCenterX + bypassRadius + maxEquipmentRadius
    const maxEquipmentDimRadius = maxEquipmentOuterDiameterIn / 2;
    sensorDimCenterX =
      bypassPositioning.bypassTubingDimCenterX +
      bypassPositioning.bypassTubingDimRadius +
      maxEquipmentDimRadius;
  } else {
    // Standard positioning when no y-tool
    sensorDimCenterX = -casingDriftRadius + maxEquipmentOuterDiameterIn / 2;
  }
  const sensorDimPoints = generateEquipmentDataPoints(
    sensorDimRadius,
    sensorDimCenterX,
    sensorDimCenterY
  );
  sensorChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Sensor OD",
      sensorDataContext.outerDiameterIn,
      sensorDataContext.lengthUnit
    ),
    xAxisLabel: `${sensorDataContext.lengthUnit.label}`,
    yAxisLabel: `${sensorDataContext.lengthUnit.label}`,
    points: sensorDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, sensorDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, sensorDataContext.lengthUnit)
      };
    })
  });
  return sensorChartDataSet;
};
